import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Select } from '@components';
import type {
  ComparisonOp,
  Condition,
  ConditionOperand,
  ConditionValue,
  ScenarioConfig,
  ScenarioIssue,
  ScenarioTool,
  ValueSource,
} from './types';
import {
  COMPARISON_OPS,
  GROUP_KINDS,
  LEAF_KINDS,
  MAX_CONDITION_DEPTH,
  OP_SYMBOLS,
  conditionKind,
  isMoment,
  newCondition,
  type ConditionKind,
} from './conditions';
import { isSystemTool, newValueSource, omitKeys, removeAt, storedFacts } from './scenarioDraft';
import { ValueSourceEditor } from './ValueSourceEditor';
import { AddButton, FieldLabel, IssueList, ListTextField, MultiSelectField, SelectField, TextField } from './ui';

const OPERAND_SOURCES: ValueSource['source'][] = ['fact', 'system', 'constant'];
const MOMENT = 'at';

/** Facts bound to a system tool always count as unchanged for its success. */
export const AutoUnchangedHint: React.FC<{ config: ScenarioConfig; tool: string }> = ({ config, tool }) => {
  const { t } = useTranslation();
  const target = config.tools.find(other => other.id === tool);
  if (!target || !isSystemTool(target)) return null;
  const bound = Object.values(target.bind);
  return bound.length ? (
    <p className="text-xs text-gray-500 dark:text-gray-400 md:col-span-2">
      {t('agents.scenario.systemTools.autoUnchanged', { facts: bound.join(', ') })}
    </p>
  ) : null;
};

interface OperandEditorProps {
  config: ScenarioConfig;
  label: string;
  value: ConditionOperand;
  onChange: (value: ConditionOperand) => void;
  issues: ScenarioIssue[];
  pointer: string;
  allowLists?: boolean;
  allowDerived: boolean;
}

const OperandEditor: React.FC<OperandEditorProps> = ({
  config,
  label,
  value,
  onChange,
  issues,
  pointer,
  allowLists = false,
  allowDerived,
}) => {
  const { t } = useTranslation();
  const momentOption = { value: MOMENT, label: t('agents.scenario.condition.moment') };
  if (!isMoment(value)) {
    return (
      <ValueSourceEditor
        config={config}
        label={label}
        value={value}
        onChange={next => onChange(next as ConditionValue)}
        issues={issues}
        pointer={pointer}
        allowLists={allowLists}
        allowDerived={allowDerived}
        sources={OPERAND_SOURCES}
        extraSources={[momentOption]}
        onExtraSource={() => onChange({ at: { date: value, time: { source: 'fact', key: '' } } })}
      />
    );
  }
  const part = (key: 'date' | 'time') => (
    <ValueSourceEditor
      config={config}
      label={t(`agents.scenario.condition.moment_${key}`)}
      value={value.at[key]}
      onChange={next => onChange({ at: { ...value.at, [key]: next as ConditionValue } })}
      issues={issues}
      pointer={`${pointer}/at/${key}`}
      allowLists={false}
      allowDerived={allowDerived}
      sources={OPERAND_SOURCES}
    />
  );
  return (
    <div>
      <FieldLabel label={label} />
      <div className="grid grid-cols-[130px_1fr] items-center gap-2">
        <Select
          size="sm"
          value={MOMENT}
          options={[
            ...OPERAND_SOURCES.map(source => ({ value: source, label: t(`agents.scenario.sources.${source}`) })),
            momentOption,
          ]}
          onChange={selected => {
            const picked = Array.isArray(selected) ? selected[0] : selected;
            if (picked !== MOMENT) onChange(newValueSource(picked as ValueSource['source'], value.at.date) as ConditionValue);
          }}
        />
        <span className="text-xs text-gray-500 dark:text-gray-400">{t('agents.scenario.condition.momentHint')}</span>
      </div>
      <div className="mt-2 space-y-2 border-l-2 border-gray-200 pl-3 dark:border-gray-700">
        {part('date')}
        {part('time')}
      </div>
      <IssueList issues={issues} pointer={pointer} />
    </div>
  );
};

interface ConditionEditorProps {
  config: ScenarioConfig;
  /** The tool that owns the requirement; it cannot require its own success. None for derived facts. */
  tool?: ScenarioTool;
  value: Condition;
  onChange: (value: Condition) => void;
  issues: ScenarioIssue[];
  pointer: string;
  /** False in the cases of a derived fact: they cannot read other derived facts. */
  allowDerived?: boolean;
  depth?: number;
  onRemove?: () => void;
}

export const ConditionEditor: React.FC<ConditionEditorProps> = ({
  config,
  tool,
  value,
  onChange,
  issues,
  pointer,
  allowDerived = true,
  depth = 1,
  onRemove,
}) => {
  const { t } = useTranslation();
  const kind = conditionKind(value);
  const kinds: ConditionKind[] = depth < MAX_CONDITION_DEPTH ? [...LEAF_KINDS, ...GROUP_KINDS] : [...LEAF_KINDS];
  const label = (key: string) => t(`agents.scenario.condition.${key}`);
  const nested = (node: Condition, path: string, change: (next: Condition) => void, remove?: () => void) => (
    <ConditionEditor
      config={config}
      tool={tool}
      value={node}
      onChange={change}
      issues={issues}
      pointer={path}
      allowDerived={allowDerived}
      depth={depth + 1}
      onRemove={remove}
    />
  );

  let body: React.ReactNode;
  if ('all' in value || 'any' in value) {
    const items = 'all' in value ? value.all : value.any;
    const group = (next: Condition[]): Condition => ('all' in value ? { all: next } : { any: next });
    body = (
      <div className="space-y-2">
        {items.map((item, index) => (
          <React.Fragment key={index}>
            {nested(
              item,
              `${pointer}/${kind}/${index}`,
              next => onChange(group(items.map((other, i) => (i === index ? next : other)))),
              items.length > 1 ? () => onChange(group(removeAt(items, index))) : undefined
            )}
          </React.Fragment>
        ))}
        <AddButton label={label('add')} onClick={() => onChange(group([...items, newCondition('compare')]))} />
        <IssueList issues={issues} pointer={`${pointer}/${kind}`} />
      </div>
    );
  } else if ('not' in value) {
    body = nested(value.not, `${pointer}/not`, next => onChange({ not: next }));
  } else if (value.op === 'tool_success') {
    body = (
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <SelectField
          label={t('agents.scenario.tool')}
          value={value.tool}
          options={config.tools
            .filter(other => other.id !== tool?.id)
            .map(other => ({ value: other.id, label: other.title || other.id }))}
          onChange={next => onChange({ ...value, tool: next })}
          issues={issues}
          pointer={`${pointer}/tool`}
        />
        <MultiSelectField
          label={t('agents.scenario.unchangedFacts')}
          values={value.unchanged}
          options={storedFacts(config).map(f => ({ value: f.name, label: f.name }))}
          onChange={unchanged => onChange({ ...value, unchanged })}
          issues={issues}
          pointer={`${pointer}/unchanged`}
        />
        <AutoUnchangedHint config={config} tool={value.tool} />
      </div>
    );
  } else if (value.op === 'in') {
    body = (
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <OperandEditor
          config={config}
          allowDerived={allowDerived}
          label={label('value')}
          value={value.value}
          onChange={operand => onChange({ ...value, value: operand })}
          issues={issues}
          pointer={`${pointer}/value`}
        />
        <ListTextField
          label={t('agents.scenario.allowedValues')}
          hint={t('agents.scenario.commaSeparated')}
          values={value.values}
          onCommit={values => onChange({ ...value, values })}
          issues={issues}
          pointer={`${pointer}/values`}
        />
      </div>
    );
  } else if (!('left' in value)) {
    body = (
      <OperandEditor
        config={config}
        allowDerived={allowDerived}
        label={label('value')}
        value={value.value}
        onChange={operand => onChange({ ...value, value: operand })}
        issues={issues}
        pointer={`${pointer}/value`}
        allowLists
      />
    );
  } else {
    body = (
      <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_90px_1fr_140px]">
        <OperandEditor
          config={config}
          allowDerived={allowDerived}
          label={label('left')}
          value={value.left}
          onChange={left => onChange({ ...value, left })}
          issues={issues}
          pointer={`${pointer}/left`}
        />
        <SelectField
          label={label('operator')}
          value={value.op}
          options={COMPARISON_OPS.map(op => ({ value: op, label: OP_SYMBOLS[op] }))}
          onChange={op => onChange({ ...value, op: op as ComparisonOp })}
          issues={issues}
          pointer={`${pointer}/op`}
        />
        <OperandEditor
          config={config}
          allowDerived={allowDerived}
          label={label('right')}
          value={value.right}
          onChange={right => onChange({ ...value, right })}
          issues={issues}
          pointer={`${pointer}/right`}
        />
        <TextField
          label={label('offset')}
          placeholder="PT60M"
          value={value.offset ?? ''}
          onChange={offset => onChange(offset ? { ...value, offset } : omitKeys(value, 'offset'))}
          issues={issues}
          pointer={`${pointer}/offset`}
          monospace
        />
      </div>
    );
  }

  return (
    <div className={depth > 1 ? 'space-y-2 border-l-2 border-primary-200 pl-3 dark:border-primary-800' : 'space-y-2'}>
      <div className="flex items-end gap-2">
        <div className="w-60">
          <SelectField
            label={label('kind')}
            value={kind}
            options={kinds.map(option => ({ value: option, label: label(`kinds.${option}`) }))}
            onChange={next => onChange(newCondition(next as ConditionKind, value))}
            issues={issues}
            pointer={`${pointer}/kind`}
          />
        </div>
        {onRemove && (
          <Button variant="ghost" size="sm" onClick={onRemove} aria-label={label('remove')}>
            <span className="material-icons text-base">delete</span>
          </Button>
        )}
      </div>
      {body}
      <IssueList issues={issues} pointer={pointer} />
    </div>
  );
};
